import React, { useState, useEffect, useCallback } from 'react';
import { Download, Search, Banknote, Building2, Lock, Unlock, CheckCircle2, CreditCard, Send, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import api from '@/utils/api';

const fmtRound = (val) => Math.round(parseFloat(val || 0)).toLocaleString('en-IN');
const monthLabel = (m) => {
  if (!m) return '';
  const d = new Date(m + '-01');
  return `${d.toLocaleString('en-IN', { month: 'long' })}, ${m.split('-')[0]}`;
};

const TransferFunds = ({ user }) => {
  const [salaries, setSalaries] = useState([]);
  const [employees, setEmployees] = useState({});
  const [costCentres, setCostCentres] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [searchCostCentre, setSearchCostCentre] = useState('');
  const [filterMonth, setFilterMonth] = useState(new Date().toISOString().slice(0, 7));

  useEffect(() => { fetchData(); }, [filterMonth]);

  const fetchData = useCallback(async () => {
    try {
      const [salRes, empRes, ccRes] = await Promise.all([
        api.get('/salary-process-v2?source=all'),
        api.get('/employees?status=Active'),
        api.get('/cost-centres'),
      ]);
      const empMap = {};
      empRes.data.forEach(e => { empMap[e.id] = e; });
      setEmployees(empMap);
      const ccMap = {};
      ccRes.data.forEach(c => { ccMap[c.id] = c.name; });
      setCostCentres(ccMap);
      const pending = salRes.data.filter(s => s.ceo_status === 'Approved' && s.month === filterMonth);
      setSalaries(pending);
      setSelectedIds([]);
    } catch (error) {
      toast.error('Failed to load data');
    } finally { setLoading(false); }
  }, [filterMonth]);

  // Filter by search
  const filtered = salaries.filter(s => {
    const emp = employees[s.employee_id] || {};
    const ccName = costCentres[emp.cost_centre_id] || '';
    const nameMatch = !searchName || (s.employee_name || '').toLowerCase().includes(searchName.toLowerCase());
    const ccMatch = !searchCostCentre || ccName === searchCostCentre;
    return nameMatch && ccMatch;
  });

  const allSelected = filtered.length > 0 && filtered.every(s => selectedIds.includes(s.id));
  const toggleSelectAll = () => setSelectedIds(allSelected ? [] : filtered.map(s => s.id));
  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const selectedSalaries = filtered.filter(s => selectedIds.includes(s.id) && !s.frozen);
  const cashSelected = selectedSalaries.filter(s => { const e = employees[s.employee_id]; return !e?.account_number || !e?.ifsc_code; });
  const hdfcSelected = selectedSalaries.filter(s => { const e = employees[s.employee_id]; return e?.account_number && e?.ifsc_code && (e?.bank_name || '').toUpperCase().includes('HDFC'); });
  const neftSelected = selectedSalaries.filter(s => { const e = employees[s.employee_id]; return e?.account_number && e?.ifsc_code && !(e?.bank_name || '').toUpperCase().includes('HDFC'); });
  const cashTotal = cashSelected.reduce((sum, s) => sum + Math.round(s.net_salary || 0), 0);
  const hdfcTotal = hdfcSelected.reduce((sum, s) => sum + Math.round(s.net_salary || 0), 0);
  const neftTotal = neftSelected.reduce((sum, s) => sum + Math.round(s.net_salary || 0), 0);

  const [sendingDirectors, setSendingDirectors] = useState(false);
  const [reversing, setReversing] = useState(false);

  const handleReverse = async () => {
    const selected = filtered.filter(s => selectedIds.includes(s.id));
    if (selected.length === 0) { toast.error('Select entries to reverse'); return; }
    if (!window.confirm(`Reverse ${selected.length} entry(ies) back to CEO for re-approval?`)) return;
    setReversing(true);
    try {
      const res = await api.post('/salary-process-v2/reverse-to-ceo', { salary_ids: selected.map(s => s.id) });
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reverse failed');
    } finally { setReversing(false); }
  };

  const handleForDirectors = async () => {
    const selected = filtered.filter(s => selectedIds.includes(s.id));
    if (selected.length === 0) { toast.error('Select employees first'); return; }
    setSendingDirectors(true);
    try {
      // Collect cost centre names from selected
      const ccNames = [...new Set(selected.map(s => {
        const emp = employees[s.employee_id] || {};
        return costCentres[emp.cost_centre_id] || '';
      }).filter(Boolean))];
      const res = await api.post('/notify/for-directors', {
        month: filterMonth,
        salary_ids: selected.map(s => s.id),
        cost_centres: ccNames,
      });
      toast.success(`${res.data.message}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send director email');
    } finally { setSendingDirectors(false); }
  };

  const handleExportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (filterMonth) params.append('month', filterMonth);
      const response = await api.get(`/export/transfer-funds-excel?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Transfer_Funds_${filterMonth || 'all'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exported');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handleDownloadNeftPdf = async () => {
    const ids = neftSelected.map(s => s.id);
    if (ids.length === 0) { toast.error('No NEFT employees selected'); return; }
    setProcessing(true);
    try {
      const response = await api.post('/transfer/neft-forms', { salary_ids: ids }, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `NEFT_Forms_${filterMonth}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('NEFT PDF downloaded');
    } catch (error) {
      toast.error('Failed to generate NEFT forms');
    }
    setProcessing(false);
  };

  const handleDownloadHdfcPdf = async () => {
    const ids = hdfcSelected.map(s => s.id);
    if (ids.length === 0) { toast.error('No HDFC employees selected'); return; }
    setProcessing(true);
    try {
      const response = await api.post('/transfer/hdfc-pdf', { salary_ids: ids, month: filterMonth }, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `HDFC_Transfer_${filterMonth}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('HDFC PDF downloaded');
    } catch (error) {
      toast.error('Failed to generate HDFC PDF');
    }
    setProcessing(false);
  };

  const handleDownloadCashPdf = async () => {
    const ids = cashSelected.map(s => s.id);
    if (ids.length === 0) { toast.error('No Cash employees selected'); return; }
    setProcessing(true);
    try {
      const response = await api.post('/transfer/cash-pdf', { salary_ids: ids, month: filterMonth }, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Cash_Salary_${filterMonth}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('Cash Salary PDF downloaded');
    } catch (error) {
      toast.error('Failed to generate Cash PDF');
    }
    setProcessing(false);
  };

  const handleBankingDone = async (salaryId, currentStatus, isFrozen) => {
    if (isFrozen) {
      if (user?.role === 'HR' || user?.role === 'Manager') {
        // HR/Manager can unfreeze individual records
        try {
          await api.post('/transfer/unfreeze', { salary_id: salaryId });
          setSalaries(prev => prev.map(s => s.id === salaryId ? { ...s, frozen: false } : s));
          toast.success('Record unfrozen');
        } catch (error) {
          toast.error('Failed to unfreeze record');
        }
      } else {
        toast.error('This record is frozen. Contact HR or Manager to unfreeze.');
      }
      return;
    }
    const done = currentStatus !== 'Paid';
    try {
      await api.post('/transfer/mark-banking-done', { salary_id: salaryId, done });
      setSalaries(prev => prev.map(s => s.id === salaryId ? { ...s, payment_status: done ? 'Paid' : 'Pending' } : s));
      toast.success(done ? 'Marked as Banking Done' : 'Unmarked Banking Done');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleFreeze = async () => {
    const paidCount = salaries.filter(s => s.payment_status === 'Paid' && !s.frozen).length;
    if (paidCount === 0) {
      toast.error('No Banking Done records to freeze');
      return;
    }
    try {
      const res = await api.post('/transfer/freeze', { month: filterMonth });
      toast.success(res.data.message);
      if (res.data.frozen_count > 0) {
        try { await api.post('/notify/payment-done', { month: filterMonth, frozen_count: res.data.frozen_count }); } catch (_) {}
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to freeze');
    }
  };

  const handleUnfreeze = async () => {
    try {
      const res = await api.post('/transfer/unfreeze', { month: filterMonth });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to unfreeze');
    }
  };

  const [razorpayConfigured, setRazorpayConfigured] = useState(false);
  const [razorpayKeyId, setRazorpayKeyId] = useState('');

  useEffect(() => {
    api.get('/razorpay/config').then(res => {
      setRazorpayConfigured(res.data.configured);
      setRazorpayKeyId(res.data.key_id);
    }).catch(() => {});
  }, []);

  const hasFrozenRecords = salaries.some(s => s.frozen);
  const hasUnfrozenPaid = salaries.some(s => s.payment_status === 'Paid' && !s.frozen);
  const canFreeze = user?.role === 'HR' || user?.role === 'Manager' || user?.role === 'Account' || user?.role === 'CEO';
  const canUnfreeze = user?.role === 'HR' || user?.role === 'Manager';

  const handleRazorpayPay = useCallback(async () => {
    const payableIds = selectedSalaries.map(s => s.id);
    if (payableIds.length === 0) {
      toast.error('No unfrozen records selected for payment');
      return;
    }
    setProcessing(true);
    try {
      const orderRes = await api.post('/razorpay/create-order', { salary_ids: payableIds });
      const { order_id, amount, currency, key_id, total_amount } = orderRes.data;

      const options = {
        key: key_id || razorpayKeyId,
        amount,
        currency,
        name: "Mr. Chow's Payroll",
        description: `Salary for ${payableIds.length} employee(s) — ₹${Math.round(total_amount).toLocaleString('en-IN')}`,
        order_id,
        handler: async (response) => {
          try {
            await api.post('/razorpay/verify-payment', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              salary_ids: payableIds,
            });
            toast.success(`Payment successful! ${payableIds.length} record(s) marked as Paid.`);
            fetchData();
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Payment verification failed');
          }
        },
        prefill: { email: user?.email || '' },
        theme: { color: '#4f46e5' },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        toast.error(`Payment failed: ${resp.error.description}`);
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create Razorpay order');
    }
    setProcessing(false);
  }, [selectedSalaries, razorpayKeyId, user, fetchData]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div></div>;
  }

  const totalNet = filtered.reduce((sum, s) => sum + (s.net_salary || 0), 0);
  const month = salaries.length > 0 ? monthLabel(salaries[0].month) : '';

  return (
    <div className="space-y-5" data-testid="transfer-funds-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Transfer Funds</h1>
          <p className="mt-1 text-base text-slate-600">
            Process bank transfers for approved salaries
            {month && <span className="ml-2 text-indigo-600 font-semibold">— Salary : {month}</span>}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          {selectedIds.length > 0 && (
            <>
              <Button onClick={handleDownloadNeftPdf} disabled={processing || neftSelected.length === 0} size="sm" className="bg-violet-600 hover:bg-violet-700 disabled:opacity-40 text-xs sm:text-sm" data-testid="download-neft-pdf-button">
                <Download className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">NEFT PDF</span><span className="sm:hidden">NEFT</span> ({neftSelected.length})
              </Button>
              <Button onClick={handleDownloadHdfcPdf} disabled={processing || hdfcSelected.length === 0} size="sm" className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-xs sm:text-sm" data-testid="hdfc-to-hdfc-button">
                <Download className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">HDFC to HDFC</span><span className="sm:hidden">HDFC</span> ({hdfcSelected.length})
              </Button>
              <Button onClick={handleDownloadCashPdf} disabled={processing || cashSelected.length === 0} size="sm" className="bg-amber-600 hover:bg-amber-700 disabled:opacity-40 text-xs sm:text-sm" data-testid="cash-transfer-button">
                <Download className="h-4 w-4 mr-1" />
                Cash ({cashSelected.length})
              </Button>
              {razorpayConfigured && (
                <Button onClick={handleRazorpayPay} disabled={processing || selectedSalaries.length === 0} className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40" data-testid="razorpay-pay-button">
                  <CreditCard className="h-4 w-4 mr-1.5" />
                  Pay via Razorpay ({selectedSalaries.length}) — ₹{fmtRound(selectedSalaries.reduce((s, r) => s + Math.round(r.net_salary || 0), 0))}
                </Button>
              )}
            </>
          )}
          {canFreeze && hasUnfrozenPaid && (
            <Button onClick={handleFreeze} className="bg-red-600 hover:bg-red-700" data-testid="freeze-button">
              <Lock className="h-4 w-4 mr-1.5" />
              Freeze Banking Done
            </Button>
          )}
          {canUnfreeze && hasFrozenRecords && (
            <Button onClick={handleUnfreeze} className="bg-blue-600 hover:bg-blue-700" data-testid="unfreeze-button">
              <Unlock className="h-4 w-4 mr-1.5" />
              Unfreeze All
            </Button>
          )}
          <Button onClick={handleExportExcel} variant="outline" size="sm" data-testid="export-transfer-excel">
            <Download className="h-4 w-4 mr-1.5" /> Export Excel
          </Button>
          {selectedIds.length > 0 && (
            <Button onClick={handleForDirectors} disabled={sendingDirectors} size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="for-directors-btn">
              <Send className="h-4 w-4 mr-1.5" /> {sendingDirectors ? 'Sending...' : `For Directors (${selectedIds.length})`}
            </Button>
          )}
          {selectedIds.length > 0 && (
            <Button onClick={handleReverse} disabled={reversing} size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700" data-testid="reverse-btn">
              <RotateCcw className="h-4 w-4 mr-1.5" /> {reversing ? 'Reversing...' : `Reverse (${selectedIds.length})`}
            </Button>
          )}
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-end gap-4 flex-wrap">
        <div className="min-w-[160px]">
          <label className="text-xs font-medium text-slate-600 mb-1 block">Month</label>
          <Input type="month" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} className="w-44" data-testid="filter-month-input" />
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="text-xs font-medium text-slate-600 mb-1 block">Search by Name</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              placeholder="Type employee name..."
              className="pl-9"
              data-testid="search-name-input"
            />
          </div>
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="text-xs font-medium text-slate-600 mb-1 block">Cost Centre</label>
          <select
            value={searchCostCentre}
            onChange={(e) => setSearchCostCentre(e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="search-cost-centre-select"
          >
            <option value="">All Cost Centres</option>
            {Object.entries(costCentres).map(([id, name]) => (
              <option key={id} value={name}>{name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
          <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900">{salaries.length === 0 ? 'No Pending Approvals' : 'No matching records'}</h3>
          <p className="text-slate-600 mt-2">{salaries.length === 0 ? 'No pending fund transfers at the moment.' : 'Try a different search.'}</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="transfer-table">
          <div className="overflow-auto">
            <table className="w-full text-xs whitespace-nowrap">
              <thead>
                <tr className="bg-slate-800 text-white">
                  <th className="px-2 py-2.5 text-center sticky left-0 bg-slate-800 z-10">
                    <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll}
                      className="border-white data-[state=checked]:bg-indigo-500 data-[state=checked]:border-indigo-500"
                      data-testid="select-all-checkbox" />
                  </th>
                  <th className="px-2 py-2.5 text-center font-semibold">Sr</th>
                  <th className="px-2 py-2.5 text-left font-semibold">Cost Centre</th>
                  <th className="px-2 py-2.5 text-left font-semibold sticky left-8 bg-slate-800 z-10">Employee</th>
                  <th className="px-2 py-2.5 text-right font-medium">Work</th>
                  <th className="px-2 py-2.5 text-right font-medium text-blue-300">W.Off</th>
                  <th className="px-2 py-2.5 text-right font-medium text-emerald-300">PL</th>
                  <th className="px-2 py-2.5 text-right font-medium text-red-300">LWP</th>
                  <th className="px-2 py-2.5 text-right font-medium text-purple-300">ODW</th>
                  <th className="px-2 py-2.5 text-right font-medium text-cyan-300">D.O.P</th>
                  <th className="px-2 py-2.5 text-right font-medium border-l border-slate-600">Basic</th>
                  <th className="px-2 py-2.5 text-right font-medium">OT</th>
                  <th className="px-2 py-2.5 text-right font-medium">Incent.</th>
                  <th className="px-2 py-2.5 text-right font-medium text-emerald-300">Gross</th>
                  <th className="px-2 py-2.5 text-right font-medium border-l border-slate-600 text-rose-300">Deduct.</th>
                  <th className="px-2 py-2.5 text-right font-semibold text-amber-300">Net Pay</th>
                  <th className="px-2 py-2.5 text-left font-semibold border-l border-slate-600">Bank Details</th>
                  <th className="px-2 py-2.5 text-center font-semibold">Banking</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((s, idx) => {
                  const emp = employees[s.employee_id] || {};
                  const ccName = costCentres[emp.cost_centre_id] || '—';
                  const isSelected = selectedIds.includes(s.id);
                  const isHDFC = emp.account_number && emp.ifsc_code && (emp.bank_name || '').toUpperCase().includes('HDFC');
                  const isCash = !emp.account_number || !emp.ifsc_code;
                  const d = <span className="text-slate-300">-</span>;
                  const v = (val) => (val > 0 ? fmtRound(val) : d);
                  const allow = s.allowances || {};
                  return (
                    <tr key={s.id} className={`hover:bg-slate-50 transition-colors ${isSelected ? 'bg-emerald-50/40' : ''}`} data-testid={`transfer-row-${s.id}`}>
                      <td className="px-2 py-2.5 text-center sticky left-0 bg-inherit z-10">
                        <Checkbox checked={isSelected} onCheckedChange={() => toggleSelect(s.id)} data-testid={`select-checkbox-${s.id}`} />
                      </td>
                      <td className="px-2 py-2.5 text-center text-slate-500 font-mono">{idx + 1}</td>
                      <td className="px-2 py-2.5 font-medium text-slate-700">{ccName}</td>
                      <td className="px-2 py-2.5 sticky left-8 bg-inherit z-10">
                        <span className="font-semibold text-slate-900">{(s.employee_name || '').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}</span> <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${s.source === 'grid2' ? 'bg-violet-100 text-violet-700' : 'bg-sky-100 text-sky-700'}`} data-testid={`sp-source-${s.id}`}>{s.source === 'grid2' ? 'SP1' : 'SP2'}</span>
                      </td>
                      <td className="px-2 py-2.5 text-right font-mono">{s.days_worked || 0}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-blue-600">{s.weekly_offs || 0}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-emerald-600">{s.paid_leaves > 0 ? s.paid_leaves : d}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-red-600">{s.lwp > 0 ? s.lwp : d}</td>
                      <td className="px-2 py-2.5 text-right font-mono text-purple-600">{s.odw_days > 0 ? s.odw_days : d}</td>
                      <td className="px-2 py-2.5 text-right font-mono font-bold">{s.dop_days || s.days_worked || 0}</td>
                      <td className="px-2 py-2.5 text-right font-mono border-l border-slate-100">{v(s.basic_salary)}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{v(s.ot_amount)}</td>
                      <td className="px-2 py-2.5 text-right font-mono">{v(allow.Incentive)}</td>
                      <td className="px-2 py-2.5 text-right font-mono font-semibold text-emerald-700">{v(s.gross_salary)}</td>
                      <td className="px-2 py-2.5 text-right font-mono font-semibold text-rose-600 border-l border-slate-100">{v(s.total_deductions)}</td>
                      <td className="px-2 py-2.5 text-right font-mono font-bold text-slate-900">₹{fmtRound(s.net_salary)}</td>
                      <td className="px-2 py-2.5 border-l border-slate-100">
                        <div className="text-[11px] space-y-0.5">
                          {emp.account_number && <span className="font-mono text-slate-600">****{emp.account_number.slice(-4)}</span>}
                          {emp.ifsc_code && <span className="font-mono text-slate-400 ml-1">{emp.ifsc_code}</span>}
                          <span className={`inline-block ml-1 px-1 py-0.5 rounded text-[8px] font-bold ${
                            isCash ? 'bg-amber-100 text-amber-700' : isHDFC ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-100 text-indigo-700'
                          }`}>{isCash ? 'Cash' : isHDFC ? 'HDFC' : 'NEFT'}</span>
                        </div>
                      </td>
                      <td className="px-2 py-2.5 text-center">
                        {s.frozen ? (
                          <div className="flex items-center justify-center gap-1 cursor-pointer" data-testid={`banking-frozen-${s.id}`}
                            onClick={() => handleBankingDone(s.id, s.payment_status, s.frozen)}>
                            <Checkbox checked disabled className="border-emerald-500 data-[state=checked]:bg-emerald-600 pointer-events-none" />
                            <Lock className={`h-3 w-3 ${canUnfreeze ? 'text-blue-500 hover:text-blue-700' : 'text-red-400'}`} />
                          </div>
                        ) : (
                          <Checkbox
                            checked={s.payment_status === 'Paid'}
                            onCheckedChange={() => handleBankingDone(s.id, s.payment_status, s.frozen)}
                            data-testid={`banking-done-${s.id}`}
                            className={s.payment_status === 'Paid' ? 'border-emerald-500 data-[state=checked]:bg-emerald-600' : ''}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-slate-100 font-semibold text-xs border-t-2 border-slate-300">
                  <td className="px-3 py-3"></td>
                  <td className="px-2 py-3"></td>
                  <td className="px-3 py-3"></td>
                  <td className="px-3 py-3 text-slate-700">TOTAL ({filtered.length} employees)</td>
                  <td className="px-3 py-3"></td>
                  <td className="px-3 py-3 text-right font-mono font-bold text-slate-900 text-sm">₹{fmtRound(totalNet)}</td>
                  <td className="px-3 py-3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

    </div>
  );
};

export default TransferFunds;
